import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { authApi } from '@/api';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { clearForcePasswordChange } from '@/store/authSlice';
import { refreshSessionInit } from '@/store/sessionSlice';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';
import { resolvePostLoginDestination } from '@/utils/postLoginNavigation';
import { useAppSelector } from '@/hooks/useAppSelector';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'At least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function RequiredPasswordChangePage() {
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const auth = useAppSelector((s) => s.auth);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      dispatch(clearForcePasswordChange());
      await dispatch(refreshSessionInit(undefined));
      reset();
      notify.t.success('toast.passwordUpdatedYouCanContinueUsingTheApp', { ns: 'auth' });
      navigate(
        resolvePostLoginDestination({
          permissions: auth.user?.permissions,
          roleCode: auth.user?.roleCode,
          enabledModules: auth.enabledModules,
          accessibleModules: auth.accessibleModules,
          entryModule: auth.entryModule,
          masterAdmin: auth.user?.masterAdmin,
          forcePasswordChange: false,
        }),
        { replace: true },
      );
    } catch (err) {
      showApiError(err, t('errors.failedToChangePassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-950">
      <div className="w-full max-w-md">
        <AuthCard title={t('pages.changeYourPassword')}>
          <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
            Your administrator requires you to set a new password before continuing.
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <AuthInput
              label={t('labels.currentPassword')}
              type="password"
              autoComplete="current-password"
              {...register('currentPassword')}
              error={errors.currentPassword?.message}
            />
            <AuthInput
              label={t('labels.newPassword')}
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
              error={errors.newPassword?.message}
            />
            <AuthInput
              label={t('labels.confirmNewPassword')}
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
              error={errors.confirmPassword?.message}
            />
            <AuthButton type="submit" loading={loading}>
              Update password
            </AuthButton>
          </form>
        </AuthCard>
      </div>
    </div>
  );
}
